import React, { Fragment, useCallback, useMemo } from 'react'
import { UseQueryResult } from '@tanstack/react-query'

import { ErrorState } from '@/components/error-state.tsx'

import { LoadingSpinner } from './loading-spinner'

export type QuerySpinnerProps = {
  /** Optional Tailwind classes for the <LoadingSpinner>. */
  spinnerClassName?: string
  /** Optional Tailwind classes for the wrapper <div>. */
  spinnerContainerClassName?: string
  spinnerHasWrapper?: boolean
  wrapperClassName?: string
  /** Children to render when none of the queries are loading/error. */
  children?: React.ReactNode
  /** A single query to observe. */
  query?: UseQueryResult
  /** Multiple queries to observe. */
  queries?: UseQueryResult[]
  /** Custom component to render while loading. */
  LoadingComponent?: (props: {
    origLoadingCmp: React.ReactNode
  }) => React.ReactNode
  ErrorComponent?: (props: {
    origErrorCmp: React.ReactNode
    onRetry: () => void
  }) => React.ReactNode
  retryingMessageVisible?: boolean
} & React.ComponentPropsWithoutRef<'div'>

/**
 * A small utility that shows a loading spinner while any of the supplied React‑Query
 * hooks are `isLoading`, an error state if any are `isError`, and otherwise renders
 * its children.
 */
export const QuerySpinner: React.FC<QuerySpinnerProps> = ({
  spinnerClassName,
  spinnerHasWrapper,
  spinnerContainerClassName,
  wrapperClassName,
  LoadingComponent,
  ErrorComponent,
  children,
  query,
  queries: queriesProp,
  retryingMessageVisible = true,
}) => {
  const queries = useMemo<UseQueryResult[]>(() => {
    if (query) return [query]
    if (queriesProp) return queriesProp
    return []
  }, [query, queriesProp])

  const isLoading = useMemo(() => queries.some((q) => q.isLoading), [queries])
  const isFetching = useMemo(() => queries.some((q) => q.isFetching), [queries])
  const isError = useMemo(() => queries.some((q) => q.isError), [queries])

  const isRetrying = useMemo(
    () => (isLoading || isFetching) && queries.some((q) => q.failureCount > 0),
    [isLoading, isFetching, queries]
  )

  const handleRetry = useCallback(() => {
    queries.forEach((q) => q.isError && q.refetch())
  }, [queries])

  if (isLoading) {
    const origLoadingCmp = (
      <LoadingSpinner
        wrapperClassName={spinnerContainerClassName}
        className={spinnerClassName}
      >
        {retryingMessageVisible && isRetrying
          ? 'Errore durante il caricamento, riprovo...'
          : null}
      </LoadingSpinner>
    )

    const Wrapper = (spinnerHasWrapper ?? wrapperClassName) ? 'div' : Fragment

    return (
      <Wrapper className={wrapperClassName}>
        {LoadingComponent?.({ origLoadingCmp }) ?? origLoadingCmp}
      </Wrapper>
    )
  }

  if (isError) {
    const origErrorCmp = <ErrorState onRetry={handleRetry} />

    return (
      ErrorComponent?.({ onRetry: handleRetry, origErrorCmp }) ?? (
        <ErrorState onRetry={handleRetry} />
      )
    )
  }

  return children
}
